// ─────────────────────────────────────────────────────────────────────────────
// Share & referral helpers.
//
// buildShareUrl(user_id)
//   Returns the canonical share URL:
//     https://bilateralcalm.com/?ref=<user_id>
//   We hardcode the marketing origin (not window.location.origin) so a
//   friend who receives a link from the iOS app or the preview URL still
//   lands on the production marketing site.
//
// shareInvite({ user_id, source })
//   Uses navigator.share() when available (iOS Safari, Android Chrome,
//   Capacitor WebView all expose it) so the user gets the native share
//   sheet. Falls back to clipboard copy + toast otherwise.
//
// captureReferrerFromUrl() / attachStoredReferrer(userId)
//   The `?ref=<user_id>` query param is captured on any page load and
//   stashed in localStorage. Once the user completes sign-in, we POST
//   `/api/referrals/attach` to bind them server-side. Idempotent.
// ─────────────────────────────────────────────────────────────────────────────

import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REF_STORAGE_KEY = "bc_referrer_user_id";
const MARKETING_ORIGIN = "https://bilateralcalm.com";

export const buildShareUrl = (userId) => {
  if (!userId) return MARKETING_ORIGIN;
  return `${MARKETING_ORIGIN}/?ref=${encodeURIComponent(userId)}`;
};

const DEFAULT_SHARE_TEXT =
  "I've been using Bilateral Calm to calm anxiety in minutes — give it a try.";

/**
 * Trigger the native share sheet or fall back to clipboard.
 * Returns { ok: boolean, method: "share" | "clipboard" | "none" }.
 */
export const shareInvite = async ({ userId, text = DEFAULT_SHARE_TEXT } = {}) => {
  const url = buildShareUrl(userId);
  const payload = {
    title: "Bilateral Calm",
    text,
    url,
  };
  // Prefer the native share sheet.
  if (typeof navigator !== "undefined" && navigator.share) {
    try {
      await navigator.share(payload);
      return { ok: true, method: "share", url };
    } catch (err) {
      // AbortError → user cancelled; treat as no-op, don't fall back.
      if (err?.name === "AbortError") {
        return { ok: false, method: "share", cancelled: true, url };
      }
      // Any other failure → fall through to clipboard.
    }
  }
  // Clipboard fallback.
  const clipboardText = `${text} ${url}`;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(clipboardText);
      return { ok: true, method: "clipboard", url };
    }
  } catch { /* fall through */ }
  // Last-ditch: legacy document.execCommand.
  try {
    const ta = document.createElement("textarea");
    ta.value = clipboardText;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
    return { ok: true, method: "clipboard", url };
  } catch {
    return { ok: false, method: "none", url };
  }
};

/** Grab ?ref= off the current URL (if any) and cache it. */
export const captureReferrerFromUrl = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const ref = (params.get("ref") || "").trim();
    if (ref && /^[A-Za-z0-9_-]{4,64}$/.test(ref)) {
      localStorage.setItem(REF_STORAGE_KEY, ref);
      return ref;
    }
  } catch { /* SSR/private mode */ }
  return null;
};

export const readStoredReferrer = () => {
  try {
    return localStorage.getItem(REF_STORAGE_KEY) || null;
  } catch {
    return null;
  }
};

export const clearStoredReferrer = () => {
  try { localStorage.removeItem(REF_STORAGE_KEY); } catch { /* ignore */ }
};

/**
 * If we have a stashed referrer AND the current user isn't the referrer
 * themselves, tell the backend to bind. No-op otherwise.
 * Returns true iff a new binding was created server-side.
 */
export const attachStoredReferrer = async (currentUserId) => {
  const ref = readStoredReferrer();
  if (!ref || !currentUserId || ref === currentUserId) {
    clearStoredReferrer();
    return false;
  }
  try {
    const res = await axios.post(`${API}/referrals/attach`, {
      referrer_user_id: ref,
    });
    clearStoredReferrer();
    return Boolean(res.data?.bound);
  } catch {
    // Leave the ref in storage so a later session can retry.
    return false;
  }
};

export const fetchMyReferralStats = async () => {
  const res = await axios.get(`${API}/referrals/me`);
  return res.data;
};
